import {
  useGetEnrollmentMultiManualReviewData,
  useUploadManualReviewDocument,
  useSubmitEnrollmentMultiManualReviewData,
} from '@egw/api'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { inAppChatService } from '@egw/app-chat'
import { Button, ButtonVariant, GenericError, Link } from '@egw/components'
import { CUSTOMER_SERVICE_EMAIL } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { Trans, useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { FileUpload, type UploadedFile } from '@egw/features-system'
import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('ManualReviewPage')

interface FileUploadComponentProps {
  applicationId: string
  onChange: (_files: UploadedFile[]) => void
  isDisabled?: boolean
}

const FileUploadComponent: FC<FileUploadComponentProps> = ({ applicationId, onChange, isDisabled }) => {
  const { t } = useTranslation('features-enrollment')
  const { mutate: uploadDocument, isPending } = useUploadManualReviewDocument(applicationId)

  return (
    <FileUpload
      onChange={onChange}
      mutate={uploadDocument}
      listTitle={t('multi.manual-review.upload_your_documents')}
      buttonLabel={t('multi.manual-review.upload_documents')}
      testId="enrollment-manual-review-file-upload"
      isDisabled={isPending || isDisabled}
    />
  )
}

export const ManualReviewPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  const { data, isLoading, error } = useGetEnrollmentMultiManualReviewData()
  const {
    mutate: submitEnrollmentMultiManualReviewData,
    isLoading: isSubmitting,
    error: submitError,
    isSuccess: isSubmitSuccess,
  } = useSubmitEnrollmentMultiManualReviewData()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  const handleSubmit = async (): Promise<void> => {
    try {
      await submitEnrollmentMultiManualReviewData({
        submittedManualReviewFilePaths: uploadedFiles.map((file) => file.filePath),
      })
    } catch (err) {
      log.error(err)
    }
  }

  return (
    <EnrollmentMultiLayout title={t('multi.manual-review.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error && applicationId ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          {!isSubmitSuccess ? (
            <>
              <PageHeader title={t('multi.manual-review.title')} />

              <p className="typo-primary-base-regular text-text-body mb-2xs">
                {t('multi.manual-review.doc_upload_description')}
              </p>
              <ul className="list-disc list-inside pl-2 typo-primary-base-regular text-text-body mb-md">
                <li>{t('multi.manual-review.doc_upload_description_1')}</li>
                <li>{t('multi.manual-review.doc_upload_description_2')}</li>
                <li>{t('multi.manual-review.doc_upload_description_3')}</li>
                <li>{t('multi.manual-review.doc_upload_description_4')}</li>
                <li>{t('multi.manual-review.doc_upload_description_5')}</li>
                <li>{t('multi.manual-review.doc_upload_description_6')}</li>
                <li>{t('multi.manual-review.doc_upload_description_7')}</li>
                <li>{t('multi.manual-review.doc_upload_description_8')}</li>
              </ul>
              <p className="typo-primary-base-regular text-text-body mb-2xs">
                {t('multi.manual-review.broker_dealer')}
              </p>
              <ul className="list-disc list-inside pl-2 typo-primary-base-regular text-text-body mb-md">
                <li>{t('multi.manual-review.broker_dealer_1')}</li>
              </ul>
              <FileUploadComponent
                applicationId={applicationId}
                onChange={(files) => {
                  setUploadedFiles(files)
                }}
                isDisabled={isSubmitting}
              />
            </>
          ) : null}

          {isSubmitSuccess ? (
            <>
              <CheckCircleIcon className="h-[4rem] w-[4rem] stroke-textSuccess stroke-[.5px] mb-sm" />
              <h2 className="typo-primary-2xl-light text-textSuccess mb-md">
                {t('multi.manual-review.document_upload_complete')}
              </h2>
              <p className="typo-primary-base text-text-body mb-md">
                {t('multi.manual-review.document_upload_complete_description')}
              </p>
            </>
          ) : null}

          <p className="typo-primary-base text-text-body inline-block mt-lg mb-md">
            <Trans t={t} i18nKey="multi.manual-review.secure_chat_description">
              <Link href={`mailto:${CUSTOMER_SERVICE_EMAIL}`}>{CUSTOMER_SERVICE_EMAIL}</Link>
            </Trans>
          </p>
          <div className="flex flex-col md:flex-row gap-xs mb-xl">
            <Button
              variant={ButtonVariant.Secondary}
              className="w-fit w-full md:w-[200px]"
              onClick={() => {
                sendEvent('bankhmulti_enroll_manual_review_started', {
                  applicationId,
                })
                inAppChatService.open()
              }}
            >
              {t('multi.manual-review.start_chat_cta')}
            </Button>
            {!isSubmitSuccess ? (
              <Button
                variant={ButtonVariant.Primary}
                className="w-fit w-full md:w-[200px]"
                isDisabled={uploadedFiles.length === 0}
                isLoading={isSubmitting}
                onClick={() => {
                  sendEvent('bankhmulti_enroll_manual_review_docs_submitted', {
                    applicationId,
                  })
                  void handleSubmit()
                }}
              >
                {t('multi.manual-review.submit')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? submitError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
